import { TableCell, StatusCell } from 'ui/tableCell';
import { Tooltip, LeftTooltip } from 'ui/tooltip';
import { GhostButton } from 'ui/button';
import { EditNew16 } from 'ui/icons/aurora';
import { localizeDateTime } from 'utils/l10n/datetime/datetime';
import { TruncateTooltip } from 'ui/utility/truncate/TruncateTooltip';
import { useModalContext } from 'utils/contexts/modal';

import type { ColumnData, AllOrganizationsModalState } from './types';

/** Columns for table shown in all orgs table */
export const COLUMNS: ColumnData[] = [
  {
    name: 'ID',
    align: 'flex-start',
    value: 'id',
    RowCell: ({ row }) => <TableCell>{row.id}</TableCell>,
  },
  {
    name: 'Created at',
    align: 'flex-start',
    value: 'createdAt',
    RowCell: ({ row }) => {
      const asDate = new Date(row.createdAt);
      return (
        <TableCell>
          <Tooltip tooltipContent={row.createdAt}>{localizeDateTime(asDate)}</Tooltip>
        </TableCell>
      );
    },
  },
  {
    name: 'Name',
    align: 'flex-start',
    value: 'name',
    RowCell: ({ row }) => (
      <TableCell>
        <TruncateTooltip name={row.name} textOnly />
      </TableCell>
    ),
  },
  {
    name: 'Website',
    align: 'flex-start',
    value: 'website',
    sortable: false,
    RowCell: ({ row }) => (
      <TableCell>
        <TruncateTooltip name={row.website} textOnly />
      </TableCell>
    ),
  },
  {
    name: 'Industry',
    align: 'flex-start',
    value: 'industry',
    RowCell: ({ row }) => (
      <TableCell>
        <TruncateTooltip name={row.industry} textOnly />
      </TableCell>
    ),
  },
  {
    name: 'Size',
    align: 'flex-start',
    value: 'estimatedSize',
    RowCell: ({ row }) => <TableCell>{row.estimatedSize}</TableCell>,
  },
  {
    name: 'Owner email',
    align: 'flex-start',
    value: 'adminUserId',
    RowCell: ({ row }) => (
      <TableCell>
        <TruncateTooltip name={row.adminUserId} textOnly />
      </TableCell>
    ),
  },
  {
    name: 'Owner name',
    align: 'flex-start',
    value: 'lastName',
    RowCell: ({ row }) => {
      const fullName = `${row.firstName} ${row.lastName}`;
      return (
        <TableCell>
          <TruncateTooltip name={fullName} textOnly />
        </TableCell>
      );
    },
  },
  {
    name: 'Phone',
    align: 'flex-start',
    value: 'phoneNumber',
    RowCell: ({ row }) => <TableCell>{row.phoneNumber}</TableCell>,
  },
  {
    name: 'Cluster ID',
    align: 'flex-start',
    value: 'clusterId',
    RowCell: ({ row }) => <TableCell>{row.clusterId}</TableCell>,
  },
  {
    name: 'Enabled',
    align: 'flex-start',
    value: 'isEnabled',
    RowCell: ({ row }) => <StatusCell showPositiveState={row.isEnabled} />,
  },
  {
    name: 'Pay thru app',
    align: 'flex-start',
    value: 'isPayThroughApp',
    RowCell: ({ row }) => <StatusCell showPositiveState={row.isPayThroughApp} />,
  },
  {
    name: 'Chat support',
    align: 'flex-start',
    value: 'showLiveChat',
    RowCell: ({ row }) => <StatusCell showPositiveState={row.showLiveChat} />,
  },
  {
    name: 'HIPAA',
    align: 'flex-start',
    value: 'dataPolicy',
    RowCell: ({ row }) => (
      <StatusCell
        data-test="table-row-data-policy-cell"
        showPositiveState={row.dataPolicy === 'Hipaa'}
      />
    ),
  },
  {
    name: '',
    align: 'flex-end',
    value: 'edit',
    sortable: false,
    RowCell: ({ row }) => {
      const { showModal } = useModalContext<AllOrganizationsModalState>();
      return (
        <TableCell>
          <LeftTooltip tooltipContent={`Edit ${row.name}`}>
            <GhostButton
              data-test="edit-organization-button"
              onClick={() => showModal({ action: 'organizationEditModal', orgId: row.id })}
            >
              <EditNew16 />
            </GhostButton>
          </LeftTooltip>
        </TableCell>
      );
    },
  },
];

/** organization values for the columns displayed in the all organizations table.
 *  This is used to created the csv values displayed when the values are exported to CSV,
 *  so that the csv matches the displayed data in the UI. Though we do filter out the "edit" column. */
export const COLUMNS_ORG_VALUES = COLUMNS.filter((column) => !!column.name).map(
  (column) => column.value,
);
